// 로컬스토리지 키 상수
export const STORAGE_KEYS = {
  INVESTMENTS: 'investment_diary_local_investments',
  PORTFOLIO_SETTINGS: 'investment_diary_local_portfolio_settings',
  ASSET_SETTINGS: 'investment_diary_local_asset_settings',
  FIXED_INCOMES: 'investment_diary_local_fixed_incomes',
  FIXED_EXPENSES: 'investment_diary_local_fixed_expenses',
  MONTHLY_BALANCES: 'investment_diary_local_monthly_balances',
} as const;

// 로컬스토리지 유틸리티 함수
export const localStorageUtils = {
  // 투자 기록 저장/조회
  getInvestments: (): any[] => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.INVESTMENTS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('로컬스토리지에서 투자 기록 읽기 실패:', error);
      return [];
    }
  },

  setInvestments: (investments: any[]): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.INVESTMENTS, JSON.stringify(investments));
    } catch (error) {
      console.error('로컬스토리지에 투자 기록 저장 실패:', error);
    }
  },

  // 포트폴리오 설정 저장/조회
  getPortfolioSettings: (): any | null => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.PORTFOLIO_SETTINGS);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('로컬스토리지에서 포트폴리오 설정 읽기 실패:', error);
      return null;
    }
  },

  setPortfolioSettings: (settings: any): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.PORTFOLIO_SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('로컬스토리지에 포트폴리오 설정 저장 실패:', error);
    }
  },

  // 자산 설정 저장/조회
  getAssetSettings: (): any | null => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.ASSET_SETTINGS);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('로컬스토리지에서 자산 설정 읽기 실패:', error);
      return null;
    }
  },

  setAssetSettings: (settings: any): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.ASSET_SETTINGS, JSON.stringify(settings));
    } catch (error) {
      console.error('로컬스토리지에 자산 설정 저장 실패:', error);
    }
  },

  // 고정 수입 저장/조회
  getFixedIncomes: (): any[] => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.FIXED_INCOMES);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('로컬스토리지에서 고정 수입 읽기 실패:', error);
      return [];
    }
  },

  setFixedIncomes: (incomes: any[]): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.FIXED_INCOMES, JSON.stringify(incomes));
    } catch (error) {
      console.error('로컬스토리지에 고정 수입 저장 실패:', error);
    }
  },

  // 고정 지출 저장/조회
  getFixedExpenses: (): any[] => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.FIXED_EXPENSES);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('로컬스토리지에서 고정 지출 읽기 실패:', error);
      return [];
    }
  },

  setFixedExpenses: (expenses: any[]): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.FIXED_EXPENSES, JSON.stringify(expenses));
    } catch (error) {
      console.error('로컬스토리지에 고정 지출 저장 실패:', error);
    }
  },

  // 월별 잔액 저장/조회
  getMonthlyBalances: (): any[] => {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.MONTHLY_BALANCES);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('로컬스토리지에서 월별 잔액 읽기 실패:', error);
      return [];
    }
  },

  setMonthlyBalances: (balances: any[]): void => {
    try {
      localStorage.setItem(STORAGE_KEYS.MONTHLY_BALANCES, JSON.stringify(balances));
    } catch (error) {
      console.error('로컬스토리지에 월별 잔액 저장 실패:', error);
    }
  },

  // 모든 로컬 데이터 삭제
  clearAll: (): void => {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }
};

// 중복 체크 유틸리티
export const duplicateCheck = {
  // 투자 기록 중복 체크 (종목명과 총 투자금액으로 판단)
  isDuplicateInvestment: (localRecord: any, dbRecords: any[]): boolean => {
    // 로컬 기록의 총 투자금액 계산
    const getTotalInvestmentAmount = (record: any): number | null => {
      if (record.type === 'BUY') {
        // 매수 기록: pricePerShare * quantity 또는 totalAmount 사용
        if (record.totalAmount !== undefined && record.totalAmount !== null) {
          return record.totalAmount;
        }
        if (record.pricePerShare && record.quantity) {
          return record.pricePerShare * record.quantity;
        }
      } else if (record.type === 'SELL') {
        // 매도 기록: 매도 금액 계산 (실제로는 매수 기록과 연결되어 있으므로 종목명으로만 비교)
        // 매도 기록은 selectedStockId를 통해 매수 기록을 찾아야 하므로, 
        // 일단 종목명만 비교하거나 매도 금액이 있으면 사용
        if (record.totalAmount !== undefined && record.totalAmount !== null) {
          return record.totalAmount;
        }
        // 매도 기록은 매수 기록과 연결되어 있으므로, 종목명만으로 비교
        return null;
      }
      return null;
    };

    const localTotalAmount = getTotalInvestmentAmount(localRecord);
    const localStockName = localRecord.stockName;

    return dbRecords.some(dbRecord => {
      // 종목명이 같아야 함
      if (dbRecord.stockName !== localStockName) {
        return false;
      }

      // 같은 타입이어야 함
      if (dbRecord.type !== localRecord.type) {
        return false;
      }

      // 총 투자금액 비교
      const dbTotalAmount = getTotalInvestmentAmount(dbRecord);
      
      // 둘 다 총 투자금액이 있으면 비교
      if (localTotalAmount !== null && dbTotalAmount !== null) {
        // 소수점 오차를 고려하여 1원 이내 차이는 동일한 것으로 간주
        return Math.abs(localTotalAmount - dbTotalAmount) < 1;
      }

      // 총 투자금액이 없는 경우 (매도 기록 등)는 종목명과 타입만으로 비교
      if (localTotalAmount === null && dbTotalAmount === null) {
        return true;
      }

      // 하나만 총 투자금액이 있으면 다른 것으로 간주
      return false;
    });
  },

  // 고정 수입 중복 체크 (이름, 일자로 판단)
  isDuplicateFixedIncome: (localIncome: any, dbIncomes: any[]): boolean => {
    return dbIncomes.some(dbIncome => 
      dbIncome.name === localIncome.name &&
      dbIncome.day === localIncome.day
    );
  },

  // 고정 지출 중복 체크 (이름, 일자로 판단)
  isDuplicateFixedExpense: (localExpense: any, dbExpenses: any[]): boolean => {
    return dbExpenses.some(dbExpense => 
      dbExpense.name === localExpense.name &&
      dbExpense.day === localExpense.day
    );
  }
};

